import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { Download, Landmark } from "lucide-react";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const eur = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + " €";

export default function TouristTaxTab() {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/registre").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, []);

  const { months, quarters, annual } = useMemo(() => {
    const m = Array.from({ length: 12 }, () => ({ nights: 0, adults: 0, tax: 0, stays: 0 }));
    rows.forEach((r) => {
      if (!r.arrivee || r.arrivee.slice(0, 4) !== String(year)) return;
      const mi = parseInt(r.arrivee.slice(5, 7), 10) - 1;
      if (mi < 0 || mi > 11) return;
      m[mi].tax += Number(r.tourist_tax) || 0;
      m[mi].adults += Number(r.adultes) || 0;
      m[mi].nights += Number(r.nights) || 0;
      m[mi].stays += 1;
    });
    const q = [0, 1, 2, 3].map((qi) => m.slice(qi * 3, qi * 3 + 3).reduce((s, x) => s + x.tax, 0));
    const a = m.reduce((s, x) => s + x.tax, 0);
    return { months: m, quarters: q, annual: a };
  }, [rows, year]);

  const exportCsv = () => {
    const head = ["Mois", "Séjours", "Nuitées", "Adultes taxés", "Taxe de séjour (€)"];
    const data = [head];
    months.forEach((x, i) => data.push([MONTHS[i], x.stays, x.nights, x.adults, x.tax.toFixed(2)]));
    data.push(["TOTAL " + year, months.reduce((s, x) => s + x.stays, 0), months.reduce((s, x) => s + x.nights, 0), months.reduce((s, x) => s + x.adults, 0), annual.toFixed(2)]);
    const csv = data.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `taxe-de-sejour-begaudiere-${year}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="tourist-tax-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {[now - 1, now, now + 1].map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`tax-year-${y}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${year === y ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-brand"}`}>{y}</button>
          ))}
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition-opacity" data-testid="tax-export-csv">
          <Download size={15} /> Export déclaration (CSV)
        </button>
      </div>

      <div className="rounded-xl border border-brand/20 bg-brand-50 p-6 flex items-center gap-4" data-testid="tax-annual-total">
        <div className="h-12 w-12 rounded-full bg-brand text-white grid place-items-center shrink-0"><Landmark size={22} strokeWidth={1.5} /></div>
        <div>
          <p className="text-sm font-semibold text-slate-600">Total à reverser à la collectivité · {year}</p>
          <p className="text-3xl font-bold tracking-tight text-slate-900">{eur(annual)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quarters.map((q, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`tax-quarter-${i + 1}`}>
            <p className="text-xs font-semibold text-slate-500">{i + 1}ᵉ trimestre</p>
            <p className="text-2xl font-bold tracking-tight text-slate-900 mt-1">{eur(q)}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <p className="p-8 text-center text-slate-400">Chargement…</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  {["Mois", "Séjours", "Nuitées", "Adultes taxés", "Taxe de séjour"].map((h) => (
                    <th key={h} className={`font-semibold px-5 py-3 whitespace-nowrap ${h === "Mois" ? "text-left" : "text-right"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {months.map((x, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`tax-month-${i}`}>
                    <td className="px-5 py-3 font-medium text-slate-900">{MONTHS[i]}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-slate-600">{x.stays}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-slate-600">{x.nights}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-slate-600">{x.adults}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-medium text-slate-900">{eur(x.tax)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold text-slate-900">
                  <td className="px-5 py-3">Total {year}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{months.reduce((s, x) => s + x.stays, 0)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{months.reduce((s, x) => s + x.nights, 0)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{months.reduce((s, x) => s + x.adults, 0)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{eur(annual)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400 leading-relaxed">
        Calcul basé sur vos réservations directes (confirmées et téléphone), la taxe étant perçue sur place à l'arrivée. Barème 2026 (taxe additionnelle 10 % incluse) : chambres d'hôtes <b>1,50 €</b>/adulte/nuit ; gîtes non classés <b>3,15 %</b> du coût de la nuitée par personne (plafond <b>4,40 €</b>). Enfants de moins de 18 ans exonérés. Les réservations issues des plateformes (Booking, Airbnb…) collectent et reversent elles-mêmes la taxe et ne sont donc pas comptées ici. Regroupement par mois d'arrivée.
      </p>
    </div>
  );
}
